import { isGradedParam } from '../../shared/tank-purpose.js';
import { implausibleReading } from '../../shared/reading-plausibility.js';

// Baseline fallback — these are the 8 built-in parameters a tank starts
// with before onboarding completes. Real values come from the server:
// admin controls the templates + per-subtype defaults, and each tank
// snapshots its own copy at creation. Use effectiveParams(tank) to read
// a tank's actual set; PARAMS only fires when the tank's params blob is
// somehow empty (legacy data, onboarding still in flight, etc.).
// Order matches how a reef-keeper actually checks: temp + salinity daily,
// the "big three" (alk, calcium, magnesium) weekly, nutrients (nitrate,
// phosphate) periodically, pH glanced at via a live probe. Alphabetical
// would put alk first — that's not how anyone uses these.
export const PARAMS = {
  temp: { label: 'Temperature', unit: '°F', target: 78.0, acceptable: [77.5, 78.5], order: 10 },
  salinity: { label: 'Salinity', unit: 'ppt', target: 35.0, acceptable: [33.5, 36.5], order: 20 },
  alk: { label: 'Alkalinity', unit: 'dKH', target: 8.5, acceptable: [8.0, 9.0], order: 30 },
  calcium: { label: 'Calcium', unit: 'ppm', target: 430, acceptable: [410, 450], order: 40 },
  magnesium: { label: 'Magnesium', unit: 'ppm', target: 1340, acceptable: [1300, 1400], order: 50 },
  nitrate: { label: 'Nitrate', unit: 'ppm', target: 5, acceptable: [1, 10], order: 60 },
  phosphate: { label: 'Phosphate', unit: 'ppm', target: 0.04, acceptable: [0.02, 0.08], order: 70 },
  ph: { label: 'pH', unit: '', target: 8.1, acceptable: [7.9, 8.3], order: 80 },
};
// SHORT, getShortLabels(), and getBaselineParams() were dropped: nothing
// imported them — components reach into useConfig().params.short_labels
// directly when they need short labels.

// Tank params are keyed alk/calcium/magnesium; the dose calculator and
// DOSE_PRODUCTS use alk/ca/mg. This map bridges the two (mirror of the
// server's DOSE_TO_TANK_PARAM in server/dose-calc.js). Without it the
// Dose tab and Home DOSE chips only matched alkalinity.
export const DOSE_TO_TANK_PARAM = { alk: 'alk', ca: 'calcium', mg: 'magnesium' };
export const TANK_TO_DOSE_PARAM = { alk: 'alk', calcium: 'ca', magnesium: 'mg' };
export const DOSABLE_TANK_PARAMS = ['alk', 'calcium', 'magnesium'];

// Reef phosphorus chemistry. Phosphate (PO₄) is the canonical stored
// unit, but reefers measure phosphorus (P) two different ways and each
// reports in a DIFFERENT unit — getting this wrong is a ~1000× error:
//   • Triton ICP "Phosphorus" row → P in mg/L (= ppm) → × 3.066        = ppm PO₄
//   • Hanna HI-736 Phosphorus ULR → P in ppb (µg/L)   → × 3.066 ÷ 1000 = ppm PO₄
// 3.066 is the molar ratio PO₄/P (94.97 / 30.97). The ICP path applies
// the bare factor in server/icp.js (its input is already ppm); this
// client helper is the Hanna quick-add path, whose checker reads ppb.
export const P_TO_PO4 = 3.066;

// Convert a raw Hanna HI-736 reading (phosphorus in ppb) to the
// canonical PO₄ ppm we store. Returns null for non-numeric input so the
// caller keeps its own validation/error path.
export function hannaPhosphorusPpbToPo4Ppm(ppb) {
  // Guard '' / null / undefined explicitly — Number('') is 0, not NaN,
  // which would silently store a real zero reading from an empty field.
  if (ppb === '' || ppb == null) return null;
  const n = Number(ppb);
  if (!Number.isFinite(n)) return null;
  return (n * P_TO_PO4) / 1000;
}

// Which phosphate test a keeper's number comes from, remembered per phone
// like the salinity scale: 'po4' (ppm phosphate: Hanna HI713, Salifert, Red
// Sea) or 'p' (Hanna HI736, phosphorus in ppb). The Log page had no way to
// say HI736, so its 23 ppb was saved as 23 ppm phosphate and the card said
// HIGH with GFO advice.
export const PHOSPHATE_KIT_KEY = 'aqs.phosphateKit';
export function readPhosphateKit() {
  try {
    return localStorage.getItem(PHOSPHATE_KIT_KEY) === 'p' ? 'p' : 'po4';
  } catch {
    return 'po4';
  }
}
export function writePhosphateKit(v) {
  try {
    localStorage.setItem(PHOSPHATE_KIT_KEY, v === 'p' ? 'p' : 'po4');
  } catch {
    /* private mode: the choice lasts this visit */
  }
}

// The value to save for a phosphate number read on `as`, with what the
// checker showed kept in the note and kit (as Home's quick add keeps it).
export function phosphateForSave(raw, as) {
  if (as !== 'p') return { value: raw, note: null, kit: null, typed: null };
  const value = hannaPhosphorusPpbToPo4Ppm(raw);
  return {
    value,
    note: `Hanna Phosphorus: ${raw} ppb`,
    kit: 'hanna-phosphorus',
    typed: `${raw} ppb phosphorus (${Math.round(value * 1000) / 1000} ppm as phosphate)`,
  };
}

// The choice is remembered per phone, so a phosphate kit's 0.05 typed with
// Hanna P still on would save as 0.00015 ppm, read LOW and advise dosing
// phosphate. The HI736 shows whole ppb (0-200), so a decimal is a ppm
// number and is refused with the way out.
export function phosphateKitProblem(raw, as) {
  if (as !== 'p' || raw === '' || raw == null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || Number.isInteger(n)) return null;
  return `${raw} looks like phosphate in ppm. The Hanna Phosphorus checker shows whole numbers in ppb: pick Phosphate ppm, or type the number the checker shows.`;
}

// Hanna sells alkalinity checkers in BOTH scales: HI772 reads dKH, HI755
// reads ppm CaCO3. A 143 from the ppm model dropped into a dKH field poisons
// everything downstream (true value 143 / 17.86 = 8.0 dKH). Convert when the
// photo read names the ppm scale, or when the magnitude makes dKH impossible:
// no tank holds 25+ dKH, while 25+ ppm CaCO3 is the ppm model's floor.
// Home quick-add and the Log page both read alk photos through this.
export function alkPhotoReadToDkh(value, unit, method = 'hanna') {
  if (value == null || !Number.isFinite(Number(value))) return { value, note: null };
  const looksPpm = /caco3|ppm/i.test(String(unit || ''));
  if (!looksPpm && Number(value) <= 25) return { value, note: null };
  return {
    value: Math.round((Number(value) / 17.86) * 10) / 10,
    note:
      method === 'hanna'
        ? `Hanna ppm CaCO3 checker: ${value} ppm`
        : `Read as ${value} ppm CaCO3, saved in dKH`,
  };
}

// Read a tank's effective parameter set, already filtered for hidden ones
// and sorted in display order. Falls back to the baseline PARAMS when the
// tank's params blob is empty. Returns an array of { key, label, unit,
// target, acceptable, order, hidden, custom }.
export function effectiveParams(tank, { includeHidden = false } = {}) {
  const raw = tank && tank.params && Object.keys(tank.params).length ? tank.params : PARAMS;
  const out = [];
  for (const [key, v] of Object.entries(raw)) {
    if (!v) continue;
    if (!includeHidden && v.hidden) continue;
    const base = PARAMS[key] || {};
    out.push({
      key,
      label: v.label || base.label || key,
      unit: v.unit ?? base.unit ?? '',
      target: typeof v.target === 'number' ? v.target : base.target,
      acceptable:
        Array.isArray(v.acceptable) && v.acceptable.length === 2 ? v.acceptable : base.acceptable,
      // Honour the snapshotted `order` whenever it's set on tank.params,
      // canonical key or not. Drag-to-reorder (see HomeView.jsx's
      // persistParamOrder) PATCHes `tank.params[key].order = N` for every
      // visible param, and ignoring those writes for canonical keys (alk,
      // temp, etc.) was silently reverting every save back to the
      // hardcoded baseline order. The baseline is used ONLY when nothing
      // is set on the tank — fresh tank, a param that the admin added
      // after the tank's snapshot, etc. Section order is the separate
      // localStorage `aqs.homeLayout` mechanism — unaffected by this.
      order: typeof v.order === 'number' ? v.order : (base.order ?? 999),
      hidden: !!v.hidden,
      custom: !!v.custom,
      product: typeof v.product === 'string' ? v.product : null,
    });
  }
  out.sort((a, b) => a.order - b.order);
  return out;
}

export function effectiveParam(tank, key) {
  const all = effectiveParams(tank, { includeHidden: true });
  return all.find((p) => p.key === key) || null;
}

// Classify a reading's value against a tank's effective acceptable range.
// Replaces paramStatus from utils.js for tank-aware views. 'tracked' is
// a quarantine tank's reef param: logged, never called high or low.
export function paramStatusFor(tank, key, value) {
  // 'implausible': a number no kit can read for this parameter (a salinity
  // S.G. saved as alkalinity), never called low and never dosed against.
  if (value != null && implausibleReading(key, value, { subtypeSlug: tank?.subtype_slug })) {
    return 'implausible';
  }
  if (!isGradedParam(tank, key)) return value == null ? 'unknown' : 'tracked';
  const p = effectiveParam(tank, key);
  if (!p || value == null || !p.acceptable) return 'unknown';
  const [lo, hi] = p.acceptable;
  if (value < lo || value > hi) return 'off';
  return 'ok';
}

// "On target" tolerance + display decimals per parameter, in CANONICAL
// storage units (dKH, ppm, ppt, °F, etc.). The eps is set just under
// the test-kit precision so a Hanna 0.1 dKH wobble reads as "on target,"
// not "0.1 over". Anything not listed falls back to a 1-decimal display
// and a 0-eps tolerance (i.e. shows the literal delta).
const DELTA_RULES = {
  alk: { eps: 0.1, decimals: 1 },
  calcium: { eps: 5, decimals: 0 },
  magnesium: { eps: 10, decimals: 0 },
  nitrate: { eps: 0.5, decimals: 1 },
  phosphate: { eps: 0.005, decimals: 3 },
  ph: { eps: 0.05, decimals: 2 },
  salinity: { eps: 0.2, decimals: 1 },
  temp: { eps: 0.5, decimals: 1 },
  // A kit reads 0.25 ppm; one place showed "0.3 over".
  ammonia: { eps: 0, decimals: 2 },
  nitrite: { eps: 0, decimals: 2 },
};

// "0.1 over" / "1.6 under" / "on target" — the one-line answer to "how
// far off am I?". Reefers think in deltas, not pairs of numbers, so the
// param card leads with this instead of "current X, target Y".
//
// `value` + `target` are in the param's canonical storage unit. The
// `displayUnits` and `salinityUnit` opts let salinity/temp render their
// delta in the user's chosen display unit (°C, S.G., …).
export function paramDeltaLabel(
  key,
  value,
  target,
  { displayUnits = {}, salinityUnit = 'ppt' } = {}
) {
  if (value == null || target == null) return null;
  const v = Number(value);
  const t = Number(target);
  if (!Number.isFinite(v) || !Number.isFinite(t)) return null;
  const rule = DELTA_RULES[key] || { eps: 0, decimals: 1 };
  if (Math.abs(v - t) <= rule.eps) return 'on target';
  const direction = v > t ? 'over' : 'under';
  let deltaText;
  if (key === 'temp' && displayUnits.temp === 'C') {
    // Δ°F → Δ°C is a pure × 5/9 (no offset).
    const dC = (Math.abs(v - t) * 5) / 9;
    deltaText = `${dC.toFixed(1)}°C`;
  } else if (key === 'salinity' && salinityUnit === 'sg') {
    // Δppt → ΔS.G. via the anchored slope (35 ppt ↔ 0.026 above 1.000).
    // Three places: what a refractometer reads. "0.0010 S.G." read as a
    // precision no hobby instrument has.
    const dSg = (Math.abs(v - t) * 0.026) / 35;
    if (dSg < 0.0005) return `slightly ${direction}`;
    deltaText = `${dSg.toFixed(3)} S.G.`;
  } else {
    deltaText = Math.abs(v - t).toFixed(rule.decimals);
  }
  return `${deltaText} ${direction}`;
}
